import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Divider, message } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api';
import MaterialCreateModal from './MaterialCreateModal';
import AccountModal from './AccountModal';
import InvoiceModalHeader from './InvoiceModalHeader';
import InvoiceModalBody from './InvoiceModalBody';
import {
  buildInvoicePayload,
  filterInvoiceItems,
  getAccountBaseId,
  getInvoiceEndpoint,
  getInvoiceFormValues,
  getInvoiceSuccessMessage,
  restoreEditedSaleStockAmounts,
} from './invoiceModalUtils';


const InvoiceModal = ({
  isVisible,
  onCancel,
  onSuccess,
  editingInvoice,
  mode,
  clientAccounts,
  materials: externalMaterials,
  saleMaterials: externalSaleMaterials,
  repositories,
  onFetchMaterials,
  onFetchSaleMaterials,
  onCreatePurchase,
  onFetchAccounts,
}) => {
  const isSale = mode === 'sale';
  const isPurchase = mode === 'purchase';

  // Internal form instance
  const [form] = Form.useForm();

  // Internal state
  const [savingLoading, setSavingLoading] = useState(false);
  const [isMaterialModalVisible, setIsMaterialModalVisible] = useState(false);
  const [materialForm] = Form.useForm();
  const [materialPrices, setMaterialPrices] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [factors, setFactors] = useState({});
  const [isAccountModalVisible, setIsAccountModalVisible] = useState(false);
  const [accountForm] = Form.useForm();
  const [activeTabKey, setActiveTabKey] = useState('items');

  // Local copies of materials
  const [materials, setMaterials] = useState([]);
  const [saleMaterials, setSaleMaterials] = useState([]);
  const hasUpdatedStockAmounts = useRef(false);

  // Update local state when external props change
  useEffect(() => {
    if (externalMaterials) {
      setMaterials(externalMaterials);
    }
  }, [externalMaterials]);

  useEffect(() => {
    if (externalSaleMaterials) {
      setSaleMaterials(externalSaleMaterials);
    }
  }, [externalSaleMaterials]);

  // Reset flag when modal closes
  useEffect(() => {
    if (!isVisible) {
      hasUpdatedStockAmounts.current = false;
    }
  }, [isVisible]);


  useEffect(() => {
    if (
      isVisible &&
      editingInvoice &&
      isSale &&
      !hasUpdatedStockAmounts.current &&
      saleMaterials.length > 0
    ) {
      setSaleMaterials(restoreEditedSaleStockAmounts(saleMaterials, editingInvoice));
      hasUpdatedStockAmounts.current = true;
    }
  }, [isVisible, editingInvoice, isSale, saleMaterials]);

  // Reset create mode and return users to the items tab whenever a modal opens.
  useEffect(() => {
    if (!isVisible) return;

    setActiveTabKey('items');

    if (!editingInvoice) {
      form.resetFields();
      form.setFieldsValue({
        Items: [],
        Vouchers: [],
      });
    }
  }, [isVisible, editingInvoice, form]);

  // Initialize edit mode from the backend shape into the form shape.
  useEffect(() => {
    if (!isVisible || !editingInvoice) return;

    form.setFieldsValue(getInvoiceFormValues(editingInvoice, materialPrices));
  }, [isVisible, editingInvoice, form, materialPrices]);

  // Handle save
  const handleSave = async () => {
    setSavingLoading(true);
    try {
      const values = form.getFieldsValue(true);
      const filteredItems = filterInvoiceItems(values.Items || []);
      const vouchers = values.Vouchers || [];

      form.setFieldsValue({ Items: filteredItems });
      const validatedValues = await form.validateFields();
      const payload = buildInvoicePayload({ validatedValues, filteredItems, vouchers });
      const endpoint = getInvoiceEndpoint({ mode, editingInvoice });

      let response;
      if (editingInvoice) {
        response = await axios.put(endpoint, payload);
      } else {
        response = await axios.post(endpoint, payload);
      }

      if (response.data.code === 0) {
        message.success(getInvoiceSuccessMessage({ mode, editingInvoice }));
        
        if (onSuccess) {
          await onSuccess();
        }
        
        onCancel();
      } else {
        message.error('فشل في حفظ الفاتورة');
      }
    } catch (error) {
      console.error('Save error:', error);
      message.error('خطأ في حفظ الفاتورة');
    } finally {
      setSavingLoading(false);
    }
  };

  // Handle cancel with confirmation
    
  const handleCancel = () => {
    const isDirty = form.isFieldsTouched();
    
    if (isDirty && !savingLoading) {
      Modal.confirm({
        title: 'هل أنت متأكد من الإغلاق؟',
        icon: <ExclamationCircleOutlined />,
        content: 'سيتم فقدان جميع التغييرات غير المحفوظة.',
        okText: 'نعم',
        cancelText: 'لا',
        onOk: () => {
          form.resetFields();
          onCancel();
        },
      });
    } else {
      form.resetFields();
      onCancel();
    }
  };

  // Handle account creation
  const handleAccountModalOk = async () => {
    try {
      const values = await accountForm.validateFields();
      const baseAccountId = getAccountBaseId(isPurchase);
      const payload = { ...values, BaseAccountId: baseAccountId };

      const response = await axios.post(`${API_BASE_URL}Account/Create`, payload);
      if (response.data.code === 0) {
        message.success('تم إنشاء الحساب بنجاح');
        setIsAccountModalVisible(false);
        accountForm.resetFields();
        if (typeof onFetchAccounts === 'function') {
          onFetchAccounts(baseAccountId);
        }
      } else {
        message.error('فشل في إنشاء الحساب');
      }
    } catch (error) {
      if (!error || !error.errorFields) {
        message.error('خطأ في حفظ الحساب');
      }
    }
  };

  // Fetch initial data when modal opens
  useEffect(() => {
    if (!isVisible) return;

    axios.get(`${API_BASE_URL}Material/GetMaterialsPrices`)
      .then(data => {
        if (data.data.code === 0) {
          setMaterialPrices(data.data.data || []);
        } else {
          message.error('فشل في جلب أسعار المواد');
        }
      })
      .catch(error => console.error('Error fetching material prices:', error));

    axios.get(`${API_BASE_URL}General/GetSupportedCurrencies`)
      .then(data => {
        if (Array.isArray(data.data)) {
          setCurrencies(data.data);
        } else {
          console.error('Unexpected currencies response shape:', data.data);
        }
      })
      .catch(error => console.error('Error fetching currencies:', error));

    axios.get(`${API_BASE_URL}General/Factors`)
      .then(data => {
        if (data.data) {
          setFactors(data.data);
        }
      })
      .catch(error => console.error('Error fetching factors:', error));
  }, [isVisible]);

  return (
    <Modal
      title={editingInvoice ? 'تعديل الفاتورة' : (isSale ? 'إضافة فاتورة بيع' : 'إضافة فاتورة شراء')}
      open={isVisible}
      onOk={handleSave}
      onCancel={handleCancel}
      destroyOnClose={true}
      confirmLoading={savingLoading}
      width="95%"
      style={{ maxWidth: 1200 }}
      bodyStyle={{ minHeight: '15vh', maxHeight: '100vh', overflowY: 'auto', overflowX:'hidden' }}
    >
      <Form form={form} layout="vertical">
        <style dangerouslySetInnerHTML={{__html: `
          .ant-select-focused .ant-select-selector,
          .ant-input:focus,
          .ant-input-number-focused .ant-input-number,
          .ant-picker-focused .ant-picker {
            box-shadow: none !important;
            border-color: #d9d9d9 !important;
          }
          .compact-items-table .ant-table-cell {
            padding: 2px 4px !important;
          }
          .compact-items-table .ant-form-item {
            margin-bottom: 0 !important;
          }
          .compact-items-table .ant-select .ant-select-selector,
          .compact-items-table .ant-input,
          .compact-items-table .ant-input-number,
          .compact-items-table .ant-picker {
            border-color: #d9d9d9 !important;
            box-shadow: none !important;
          }
          .compact-items-table .ant-select-focused .ant-select-selector,
          .compact-items-table .ant-input:focus,
          .compact-items-table .ant-input-number-focused,
          .compact-items-table .ant-input-number:focus,
          .compact-items-table .ant-picker-focused,
          .compact-items-table .ant-picker:focus {
            border-color: #d9d9d9 !important;
            box-shadow: none !important;
            outline: none !important;
          }
          .compact-items-table .ant-select:hover .ant-select-selector,
          .compact-items-table .ant-input:hover,
          .compact-items-table .ant-input-number:hover,
          .compact-items-table .ant-picker:hover {
            border-color: #d9d9d9 !important;
          }
          .compact-items-table .ant-form-item .ant-form-item-control-input {
            min-height: auto !important;
          }
          .ant-tabs-nav .ant-tabs-tab {
            margin: 0 12px 0 0 !important;
          }
          .ant-tabs-nav .ant-tabs-tab:last-child {
            margin-right: 12px !important;
          }
        `}} />

        <InvoiceModalHeader
          isPurchase={isPurchase}
          editingInvoice={editingInvoice}
          clientAccounts={clientAccounts}
          repositories={repositories}
          onAddAccount={() => setIsAccountModalVisible(true)}
        />

        <Divider style={{ margin: '0px 0'}}>تفاصيل الفاتورة</Divider>

        <InvoiceModalBody
          form={form}
          mode={mode}
          isSale={isSale}
          activeTabKey={activeTabKey}
          onTabChange={setActiveTabKey}
          materials={materials}
          saleMaterials={saleMaterials}
          currencies={currencies}
          factors={factors}
          materialPrices={materialPrices}
          onFetchMaterials={onFetchMaterials}
          onFetchSaleMaterials={onFetchSaleMaterials}
          onAddMaterial={() => setIsMaterialModalVisible(true)}
          onCreatePurchase={onCreatePurchase}
        />
      </Form>

      {/* Sub-modals */}
      <MaterialCreateModal
        open={isMaterialModalVisible}
        onCancel={() => setIsMaterialModalVisible(false)}
        onSuccess={() => {
          onFetchMaterials('');
        }}
        form={materialForm}
        editingMaterial={null}
        keepOpenAfterSave={true}
      />
      
      <AccountModal
        isVisible={isAccountModalVisible}
        onOk={handleAccountModalOk}
        onCancel={() => setIsAccountModalVisible(false)}
        editingAccount={null}
        form={accountForm}
      />
    </Modal>
  );
};

export default InvoiceModal;